<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  shallowRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  area: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['save', 'cancel']);

const { t } = useI18n();

const T = key => t(`LIBRARIES.EDIT.ADVANCED_SETTINGS.CUSTOM_GEO_AREAS.${key}`);

const name = ref('');
const description = ref('');
const areaType = ref('bounding_box');
const mapContainer = ref(null);
const drawnLayer = shallowRef(null);
const areaTooLarge = ref(false);

// ~30,000 km² ≈ Belgium's surface area
const MAX_AREA_KM2 = 30000;

// Leaflet instances (lazy-loaded)
let L = null;
let map = null;

const AREA_TYPES = [
  { value: 'bounding_box', label: 'BOUNDING_BOX' },
  { value: 'polygon', label: 'POLYGON' },
  { value: 'radius', label: 'RADIUS' },
];

const clearDrawnLayer = () => {
  if (drawnLayer.value && map) {
    map.removeLayer(drawnLayer.value);
    drawnLayer.value = null;
  }
  areaTooLarge.value = false;
};

const handleLayerCreated = e => {
  // Remove previous drawn layer
  clearDrawnLayer();
  drawnLayer.value = e.layer;

  // Disable draw mode after one shape
  map.pm.disableDraw();

  // Enable editing on the newly created layer
  e.layer.pm.enable();

  // Check area size
  checkAreaSize(e.layer);

  // Re-check on edit
  e.layer.on('pm:edit', () => checkAreaSize(e.layer));
};

const addExistingShape = () => {
  if (!props.area || !L || !map) return;

  const { area_type: type } = props.area;
  let layer = null;

  if (type === 'bounding_box' && props.area.bounding_box) {
    const { top_left: tl, bottom_right: br } = props.area.bounding_box;
    const bounds = L.latLngBounds(
      L.latLng(tl.lat, tl.lon),
      L.latLng(br.lat, br.lon)
    );
    layer = L.rectangle(bounds, { color: '#3b82f6', weight: 2 });
  } else if (type === 'polygon' && props.area.polygon?.points) {
    const latlngs = props.area.polygon.points.map(p => L.latLng(p.lat, p.lon));
    layer = L.polygon(latlngs, { color: '#3b82f6', weight: 2 });
  } else if (type === 'radius' && props.area.radius) {
    const { center, radius: r } = props.area.radius;
    layer = L.circle(L.latLng(center.lat, center.lon), {
      radius: r,
      color: '#3b82f6',
      weight: 2,
    });
  }

  if (layer) {
    layer.addTo(map);
    layer.pm.enable();
    drawnLayer.value = layer;
    checkAreaSize(layer);
    layer.on('pm:edit', () => checkAreaSize(layer));
    map.fitBounds(layer.getBounds());
  }
};

const extractGeometry = () => {
  if (!drawnLayer.value) return null;

  const layer = drawnLayer.value;

  if (areaType.value === 'bounding_box') {
    const bounds = layer.getBounds();
    return {
      bounding_box: {
        top_left: {
          lat: Math.round(bounds.getNorth() * 1e6) / 1e6,
          lon: Math.round(bounds.getWest() * 1e6) / 1e6,
        },
        bottom_right: {
          lat: Math.round(bounds.getSouth() * 1e6) / 1e6,
          lon: Math.round(bounds.getEast() * 1e6) / 1e6,
        },
      },
    };
  }

  if (areaType.value === 'polygon') {
    const latlngs = layer.getLatLngs()[0];
    const points = latlngs.map(ll => ({
      lat: Math.round(ll.lat * 1e6) / 1e6,
      lon: Math.round(ll.lng * 1e6) / 1e6,
    }));
    // Close the polygon — API requires first point == last point
    if (
      points.length &&
      (points[0].lat !== points[points.length - 1].lat ||
        points[0].lon !== points[points.length - 1].lon)
    ) {
      points.push({ ...points[0] });
    }
    return {
      polygon: { points },
    };
  }

  if (areaType.value === 'radius') {
    const center = layer.getLatLng();
    return {
      radius: {
        center: {
          lat: Math.round(center.lat * 1e6) / 1e6,
          lon: Math.round(center.lng * 1e6) / 1e6,
        },
        radius: Math.round(layer.getRadius()),
      },
    };
  }

  return null;
};

const canSave = computed(() => !!name.value.trim() && !!drawnLayer.value);

const computeAreaKm2 = layer => {
  if (layer.getRadius) {
    // Circle: π × r²
    const r = layer.getRadius(); // meters
    return (Math.PI * r * r) / 1e6;
  }

  // Rectangle or Polygon — use Leaflet's geodesicArea if available
  const latlngs = layer.getLatLngs
    ? Array.isArray(layer.getLatLngs()[0])
      ? layer.getLatLngs()[0]
      : layer.getLatLngs()
    : [];

  if (!latlngs.length || !L) return 0;

  // L.GeometryUtil.geodesicArea expects an array of LatLng
  if (L.GeometryUtil?.geodesicArea) {
    return Math.abs(L.GeometryUtil.geodesicArea(latlngs)) / 1e6;
  }

  // Fallback: shoelace on a plane approximation
  const toRad = d => (d * Math.PI) / 180;
  let area = 0;
  for (let i = 0; i < latlngs.length; i++) {
    const j = (i + 1) % latlngs.length;
    area +=
      toRad(latlngs[j].lng - latlngs[i].lng) *
      (2 + Math.sin(toRad(latlngs[i].lat)) + Math.sin(toRad(latlngs[j].lat)));
  }
  // Earth radius² × |area| / 2, in km²
  return (Math.abs(area) * 6371 * 6371) / 2;
};

const checkAreaSize = layer => {
  areaTooLarge.value = computeAreaKm2(layer) > MAX_AREA_KM2;
};

const onSave = () => {
  if (!canSave.value) return;

  const geometry = extractGeometry();
  if (!geometry) return;

  emit('save', {
    name: name.value.trim(),
    description: description.value.trim() || undefined,
    area_type: areaType.value,
    ...geometry,
  });
};

const initMap = async () => {
  // Lazy-load Leaflet and Geoman
  const [leafletModule] = await Promise.all([
    import('leaflet'),
    import('leaflet/dist/leaflet.css'),
  ]);
  L = leafletModule.default || leafletModule;

  // Import Geoman (side-effect: extends L.Map prototype)
  await import('@geoman-io/leaflet-geoman-free');
  await import('@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css');

  await nextTick();
  if (!mapContainer.value) return;

  map = L.map(mapContainer.value, {
    center: [50.5, 4.5],
    zoom: 7,
    zoomControl: true,
  });

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19,
  }).addTo(map);

  // Configure Geoman — only show the draw button for the active area type
  map.pm.addControls({
    position: 'topleft',
    drawMarker: false,
    drawCircleMarker: false,
    drawPolyline: false,
    drawText: false,
    drawRectangle: areaType.value === 'bounding_box',
    drawPolygon: areaType.value === 'polygon',
    drawCircle: areaType.value === 'radius',
    editMode: false,
    dragMode: false,
    cutPolygon: false,
    removalMode: false,
    rotateMode: false,
  });

  // Listen for layer creation
  map.on('pm:create', handleLayerCreated);

  // If editing, add the existing shape
  if (props.area) {
    addExistingShape();
  }
};

const updateDrawControls = () => {
  if (!map) return;

  // Clear any existing drawn layer when type changes
  clearDrawnLayer();

  map.pm.addControls({
    drawText: false,
    drawRectangle: areaType.value === 'bounding_box',
    drawPolygon: areaType.value === 'polygon',
    drawCircle: areaType.value === 'radius',
  });
};

watch(areaType, () => {
  updateDrawControls();
});

onMounted(() => {
  if (props.area) {
    name.value = props.area.name || '';
    description.value = props.area.description || '';
    areaType.value = props.area.area_type || 'bounding_box';
  }
  // Short delay to ensure the DOM is rendered before initializing map
  setTimeout(() => initMap(), 100);
});

onBeforeUnmount(() => {
  if (map) {
    map.off('pm:create', handleLayerCreated);
    map.remove();
    map = null;
  }
});
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    @click.self="emit('cancel')"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto"
    >
      <div class="p-4 border-b border-slate-200 dark:border-slate-700">
        <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100">
          {{ area ? T('EDIT_AREA') : T('ADD_AREA') }}
        </h3>
      </div>

      <div class="p-4 space-y-4">
        <!-- Name -->
        <Input
          v-model="name"
          :label="T('NAME_LABEL')"
          type="text"
          :placeholder="T('NAME_PLACEHOLDER')"
        />

        <!-- Description -->
        <Input
          v-model="description"
          :label="T('DESCRIPTION_LABEL')"
          type="text"
          :placeholder="T('DESCRIPTION_PLACEHOLDER')"
        />

        <!-- Area type -->
        <div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ T('AREA_TYPE_LABEL') }}
          </label>
          <select
            v-model="areaType"
            class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option
              v-for="type in AREA_TYPES"
              :key="type.value"
              :value="type.value"
            >
              {{ T(type.label) }}
            </option>
          </select>
        </div>

        <!-- Draw instruction -->
        <p class="text-xs text-slate-500 dark:text-slate-400">
          <template v-if="areaType === 'bounding_box'">
            {{ T('DRAW_BOUNDING_BOX') }}
          </template>
          <template v-else-if="areaType === 'polygon'">
            {{ T('DRAW_POLYGON') }}
          </template>
          <template v-else>
            {{ T('DRAW_RADIUS') }}
          </template>
        </p>

        <!-- Map -->
        <div
          ref="mapContainer"
          class="geo-map-container w-full h-80 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden"
        />

        <!-- Area too large warning -->
        <div
          v-if="areaTooLarge"
          class="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700"
        >
          <span class="text-amber-600 dark:text-amber-400 text-sm leading-5">
            ⚠
          </span>
          <p class="text-xs text-amber-700 dark:text-amber-300">
            {{ T('AREA_TOO_LARGE_WARNING') }}
          </p>
        </div>
      </div>

      <div
        class="flex justify-end gap-2 p-4 border-t border-slate-200 dark:border-slate-700"
      >
        <NextButton
          md
          slate
          ghost
          :label="T('CANCEL')"
          @click="emit('cancel')"
        />
        <NextButton
          md
          :label="T('SAVE')"
          :disabled="!canSave"
          @click="onSave"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/*
 * Tailwind preflight resets that break Leaflet / Geoman:
 *   img   → max-width:100%; display:block
 *   a     → color:inherit; text-decoration:inherit
 *   button→ background:transparent; padding:0; border:none
 *   svg   → display:block
 *   *     → border-width:0; box-sizing:border-box
 *
 * Also: Modal.vue applies a global `a { padding: 1rem }` inside
 * .modal-container form — must override with !important.
 */

/* Kill the modal's blanket padding on ALL anchors inside the map */
.geo-map-container :deep(a) {
  padding: 0 !important;
}

/* Tile images must not be constrained */
.geo-map-container :deep(img) {
  max-width: none !important;
  max-height: none !important;
}

/* Leaflet control anchors (zoom, attribution, geoman toolbar) */
.geo-map-container :deep(.leaflet-control a) {
  background-color: #fff;
  color: #333;
  text-decoration: none;
  cursor: pointer;
}

/* Zoom control sizing — 26px for a compact look */
.geo-map-container :deep(.leaflet-control-zoom a) {
  width: 26px;
  height: 26px;
  line-height: 26px;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  display: block;
}

/* Geoman toolbar: button-container holds an <a> with a .control-icon <img> */
.geo-map-container :deep(.leaflet-pm-toolbar .button-container) {
  position: relative;
}

.geo-map-container :deep(.leaflet-pm-toolbar .button-container a) {
  background-color: #fff;
  text-decoration: none;
  display: block;
  position: relative;
  width: 26px;
  height: 26px;
  cursor: pointer;
  border: none;
  overflow: hidden;
}

.geo-map-container :deep(.leaflet-pm-toolbar .button-container a:hover) {
  background-color: #f4f4f4;
}

/* Icon image inside each toolbar button */
.geo-map-container :deep(.leaflet-pm-toolbar .control-icon) {
  display: block;
  width: 26px;
  height: 26px;
  padding: 3px;
  box-sizing: border-box;
}

/* Geoman action labels ("Finish", "Cancel", etc.) */
.geo-map-container :deep(.leaflet-pm-actions-container a) {
  background-color: #242424;
  color: #fff;
  text-decoration: none;
  cursor: pointer;
  padding: 2px 8px !important;
  white-space: nowrap;
  font-size: 12px;
  line-height: 20px;
}

/* Restore border rendering for Leaflet controls */
.geo-map-container :deep(.leaflet-control) {
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  background-clip: padding-box;
}

.geo-map-container :deep(.leaflet-control-zoom a + a) {
  border-top: 1px solid #ccc;
}

/* SVG overlays (drawn shapes, etc.) */
.geo-map-container :deep(svg) {
  display: inline;
  overflow: hidden;
}

/* Fix Leaflet <button> elements */
.geo-map-container :deep(button) {
  background-color: #fff;
  border: none;
  cursor: pointer;
}
</style>
